import ReactModal from 'react-modal';
import General from '../utils/GeneralUtils';
import './TitleFieldsSlideout.scss'; // Reuse the same styles

interface InstitutionFieldsSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  fieldKey: string | null;
}

// Institution type values
const INSTITUTION_TYPES = [
  { value: 'DISTRICT', description: 'School district or local education agency (LEA)' },
  { value: 'SCHOOL', description: 'Individual school (public or private)' },
  { value: 'AGENCY', description: 'Education service agency or regional organization' },
];

// Institution subtype values
const INSTITUTION_SUBTYPES = [
  { value: 'REGULAR', description: 'Standard public school district' },
  { value: 'CHARTER', description: 'Charter school authorizer or management organization' },
  { value: 'SPECIALIZED', description: 'Specialized district (e.g., vocational, special education)' },
  { value: 'SERVICE', description: 'Service agency providing support to other districts' },
  { value: 'OTHER', description: 'Other district type' },
  { value: 'SUPERVISORY', description: 'Supervisory union or administrative unit' },
  { value: 'STATE', description: 'State-operated district or school' },
  { value: 'COMPONENT', description: 'Component district within a larger administrative unit' },
  { value: 'FEDERAL', description: 'Federally-operated school (e.g., Bureau of Indian Education)' },
];

// Institution model values (school-specific characteristics)
const INSTITUTION_MODELS = [
  { value: 'ALTERNATIVE', description: 'Alternative education program' },
  { value: 'CHARTER', description: 'Charter school' },
  { value: 'EARLY_CHILDHOOD_DAY_CARE', description: 'Early childhood or day care center' },
  { value: 'MILITARY', description: 'Military or Department of Defense school' },
  { value: 'MONTESSORI', description: 'Montessori method school' },
  { value: 'ONLINE', description: 'Virtual or online school' },
  { value: 'SPECIAL_EDUCATION', description: 'Special education focused school' },
  { value: 'SPECIAL_PROGRAM_EMPHASIS', description: 'School with special program emphasis (e.g., STEM, arts)' },
  { value: 'VOCATIONAL_TECHNICAL', description: 'Vocational or technical education school' },
  { value: 'WALDORF', description: 'Waldorf/Steiner education school' },
];

// Institution access values
const INSTITUTION_ACCESS = [
  { value: 'PUBLIC', description: 'Public school or district' },
  { value: 'PRIVATE', description: 'Private school' },
];

// Religious affiliation values
const RELIGIOUS_AFFILIATIONS = [
  { value: 'NONE', description: 'No religious affiliation (secular)' },
  { value: 'CATHOLIC', description: 'Roman Catholic' },
  { value: 'BAPTIST', description: 'Baptist denomination' },
  { value: 'LUTHERAN', description: 'Lutheran denomination' },
  { value: 'EPISCOPAL', description: 'Episcopal/Anglican' },
  { value: 'METHODIST', description: 'Methodist/United Methodist' },
  { value: 'PRESBYTERIAN', description: 'Presbyterian' },
  { value: 'JEWISH', description: 'Jewish' },
  { value: 'CHRISTIAN', description: 'Christian (non-denominational or other)' },
  { value: 'ISLAMIC', description: 'Islamic/Muslim' },
  { value: 'ADVENTIST', description: 'Seventh-day Adventist' },
  { value: 'FRIENDS', description: 'Quaker/Society of Friends' },
  { value: 'MENNONITE', description: 'Mennonite' },
  { value: 'ASSEMBLY_OF_GOD', description: 'Assembly of God' },
  { value: 'OTHER', description: 'Other religious affiliation' },
];

// Field configuration map
const FIELD_CONFIG: Record<string, { title: string; description: string; values: { value: string; description: string }[] }> = {
  type: {
    title: 'Institution Type',
    description: 'The primary classification of the institution.',
    values: INSTITUTION_TYPES,
  },
  subtype: {
    title: 'Institution Subtype',
    description: 'Further classification of the institution type, particularly for districts.',
    values: INSTITUTION_SUBTYPES,
  },
  model: {
    title: 'Institution Model',
    description: 'The educational model or special characteristics of the school.',
    values: INSTITUTION_MODELS,
  },
  religiousAffiliation: {
    title: 'Religious Affiliation',
    description: 'The religious denomination or affiliation of the institution (primarily for private schools).',
    values: RELIGIOUS_AFFILIATIONS,
  },
  access: {
    title: 'Access',
    description: 'Whether the institution is publicly or privately funded.',
    values: INSTITUTION_ACCESS,
  },
};

export function InstitutionFieldsSlideout({ isOpen, onClose, fieldKey }: InstitutionFieldsSlideoutProps) {
  const config = fieldKey ? FIELD_CONFIG[fieldKey] : null;

  if (!config) {
    return null;
  }

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      style={General.getSliderDefaults({ width: 'min(90vw, 550px)' })}
      parentSelector={General.getModalRoot}
    >
      <div className="title-fields-slideout">
        <div className="header">
          <h3>{config.title} Values</h3>
        </div>

        <div className="section intro">
          <p>{config.description}</p>
        </div>

        <div className="section">
          <div className="values-grid values-grid--with-descriptions">
            {config.values.map(({ value, description }) => (
              <div key={value} className="value-row">
                <span className="desc-12">{value}</span>
                <span className="value-description">{description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ReactModal>
  );
}

// Export the field keys that should trigger this slideout
export const INSTITUTION_FIELD_KEYS = ['type', 'subtype', 'model', 'religiousAffiliation', 'access'];
